/**
 * LocalStorage utility functions
 * Centralized storage operations for better maintainability
 */

const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  USER_DATA: 'userData',
  DEALER_PAYMENT_STATUS: 'dealerPaymentStatus',
} as const;

export const storage = {
  /**
   * Get authentication token from localStorage
   */
  getAuthToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Set authentication token in localStorage
   */
  setAuthToken: (token: string): void => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  /**
   * Remove authentication token from localStorage
   */
  removeAuthToken: (): void => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Get user data from localStorage
   */
  getUserData: (): any | null => {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (!userData) return null;
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  },

  /**
   * Set user data in localStorage
   */
  setUserData: (user: any): void => {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  },

  /**
   * Remove user data from localStorage
   */
  removeUserData: (): void => {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  },

  /**
   * Get dealer payment status from localStorage
   */
  getDealerPaymentStatus: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.DEALER_PAYMENT_STATUS);
  },

  /**
   * Set dealer payment status in localStorage
   */
  setDealerPaymentStatus: (status: string): void => {
    localStorage.setItem(STORAGE_KEYS.DEALER_PAYMENT_STATUS, status);
  },

  /**
   * Remove dealer payment status from localStorage
   */
  removeDealerPaymentStatus: (): void => {
    localStorage.removeItem(STORAGE_KEYS.DEALER_PAYMENT_STATUS);
  },

  /**
   * Clear all authentication-related data
   */
  clearAuth: (): void => {
    storage.removeAuthToken();
    storage.removeUserData();
    storage.removeDealerPaymentStatus();
    // Also clear sessionStorage
    sessionStorage.clear();
  },

  /**
   * Clear ALL storage types - use this for account deletion
   * Clears localStorage, sessionStorage, cookies, and any persisted form data
   */
  clearAllStorage: (): void => {
    // Clear all localStorage
    localStorage.clear();

    // Clear all sessionStorage
    sessionStorage.clear();

    // Clear any cookies (if used)
    // Note: Cookies require domain/path, so we clear common ones
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      // Clear cookie by setting it to expire in the past
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    });
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!storage.getAuthToken() && !!storage.getUserData();
  },
};
